import { useState, useMemo, useEffect } from 'react';
import { Title } from 'react-admin';
import {
  Alert, Box, Typography, Card, CardContent, Grid2 as Grid,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Table, TableBody, TableCell, TableHead, TableRow,
  Skeleton, Tooltip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend,
} from 'recharts';
import {
  canonicalOrgPath,
  isOrgPathWithin,
  useRollup,
  useTrend,
  exportToCsv,
} from '../hooks/useRollup';
import type { RollupResponse } from '../fixtures/analytics-fixture';
import { PARTICIPATION_COUNT_METRIC, type ParticipationCount } from '../utils/participation-count';

const METRICS = [
  { value: 'pass_rate',     label: 'Pass Rate' },
  { value: 'attempt_count', label: 'Attempt Count' },
  { value: PARTICIPATION_COUNT_METRIC, label: 'Existing participation record count' },
];

const PERIODS_AVAILABLE = [
  '2025-10', '2025-11', '2025-12',
  '2026-01', '2026-02', '2026-03', '2026-04', '2026-05', '2026-06',
];

const pct = (v: number, metric: string) =>
  metric === 'pass_rate' ? `${Math.round(v * 100)}%` : String(Math.round(v));

export function AnalystDashboard({ defaultOrgPath }: { defaultOrgPath: string }) {
  const [metric, setMetric] = useState('pass_rate');
  const [period, setPeriod] = useState('2026-06');
  const [orgPath, setOrgPath] = useState(defaultOrgPath);
  const [compare, setCompare] = useState<'children' | undefined>('children');
  const [trendRefresh, setTrendRefresh] = useState(0);
  const queryOrgPath = canonicalOrgPath(orgPath) === orgPath &&
    isOrgPathWithin(defaultOrgPath, orgPath) ? orgPath : null;

  const { data, loading, error, reload } = useRollup(queryOrgPath ?? '', metric, period, compare);
  const { trend, loading: trendLoading, error: trendError } = useTrend(
    queryOrgPath ?? '',
    metric,
    PERIODS_AVAILABLE,
    trendRefresh,
  );

  useEffect(() => {
    setOrgPath(defaultOrgPath);
  }, [defaultOrgPath]);

  const isParticipation=metric===PARTICIPATION_COUNT_METRIC;
  const tableRows = useMemo(() => {
    if (!data || loading || error || !queryOrgPath || data.orgPath !== queryOrgPath) return [];
    if (data.metric === PARTICIPATION_COUNT_METRIC) {
      const count = data as ParticipationCount;
      return [
        { ...count.total, level: 'root', sum: undefined, avg: undefined },
        ...(count.children ?? [])
          .filter((c) => isOrgPathWithin(queryOrgPath, c.orgPath))
          .map((c) => ({ ...c, level: 'child', sum: undefined, avg: undefined })),
      ];
    }
    const regular = data as RollupResponse;
    return [
      { orgPath: regular.orgPath, level: 'root', ...regular.total },
      ...(regular.children ?? [])
        .filter((c) => isOrgPathWithin(queryOrgPath, c.orgPath))
        .map((c) => ({ orgPath: c.orgPath, level: 'child', ...c.total })),
    ];
  }, [data, loading, error, queryOrgPath]);

  const handleExport = () => {
    if(loading||error||!data||!tableRows.length)return;
    const rows=tableRows.map(r=>isParticipation?{orgPath:r.orgPath,level:r.level,metric,period,count:r.count,provenance:(data as ParticipationCount).provenance,archived_records:(data as ParticipationCount).archived_records,time_basis:(data as ParticipationCount).time_basis,complete_participation_history:false}:{orgPath:r.orgPath,level:r.level,metric,period,count:r.count,sum:r.sum,avg:r.avg});
    exportToCsv(rows,`rollup_${metric}_${period}.csv`);
  };
  const trendData = trendLoading||trendError?[]:trend;
  const isRate = metric === 'pass_rate';

  return (
    <Box p={3}>
      <Title title="Analytics Explorer" />

      {/* Selectors */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Query Parameters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="analytics-metric-label">Metric</InputLabel>
                <Select
                  value={metric}
                  label="Metric"
                  labelId="analytics-metric-label"
                  onChange={(e) => setMetric(e.target.value)}
                >
                  {METRICS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="analytics-period-label">Period</InputLabel>
                <Select
                  value={period}
                  label="Period"
                  labelId="analytics-period-label"
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  {PERIODS_AVAILABLE.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Org Path"
                value={orgPath}
                onChange={(e) => setOrgPath(e.target.value)}
                error={!queryOrgPath}
                helperText={queryOrgPath
                  ? `Limited to ${defaultOrgPath} and descendants`
                  : `Enter ${defaultOrgPath} or one of its descendants`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="analytics-compare-label">Compare</InputLabel>
                <Select
                  value={compare ?? 'none'}
                  label="Compare"
                  labelId="analytics-compare-label"
                  onChange={(e) =>
                    setCompare(e.target.value === 'children' ? 'children' : undefined)
                  }
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="children">Children</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 1 }}>
              <Tooltip title="Export CSV">
                <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  disabled={loading||!!error||!tableRows.length}
                  fullWidth
                >
                  CSV
                </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error&&<Alert severity="error">{error}</Alert>}
      <Button onClick={() => { reload(); setTrendRefresh(v => v + 1); }} disabled={loading}>Reload current query</Button>
      {isParticipation&&<Alert severity="info">Existing record count — unverified provenance; archived records excluded. Period uses source-reported created_at. This is not complete participation history, a count of new declarations, clinical clearance or official exclusion.</Alert>}
      {isParticipation&&data&&!loading&&!error&&data.total.count===0&&<Typography>No qualifying source records. Coverage remains incomplete.</Typography>}
      {trendError&&<Alert severity="warning">{trendError}</Alert>}
      {/* Trend chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {METRICS.find((m) => m.value === metric)?.label} — Trend ({orgPath})
          </Typography>
          {trendLoading
            ? <Skeleton variant="rectangular" height={240} />
            : (
              <ResponsiveContainer width="100%" height={240}>
                {isRate ? (
                  <AreaChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1565c0" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#1565c0" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis
                      tickFormatter={(v) => `${Math.round(v * 100)}%`}
                      domain={[0.5, 1.0]}
                      tick={{ fontSize: 12 }}
                    />
                    <ChartTooltip formatter={(v) => typeof v === 'number' ? pct(v, metric) : v} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name={METRICS.find((m) => m.value === metric)?.label}
                      stroke="#1565c0"
                      fill="url(#trendGrad)"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </AreaChart>
                ) : (
                  <LineChart data={trendData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <ChartTooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={METRICS.find((m) => m.value === metric)?.label}
                      stroke="#1565c0"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
        </CardContent>
      </Card>

      {/* Data table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Raw Results — {period}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={loading||!!error||!tableRows.length}
            >
              Export CSV
            </Button>
          </Box>
          {loading
            ? <Skeleton variant="rectangular" height={160} />
            : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Org Path</TableCell>
                    <TableCell align="right">Count</TableCell>
                    {!isParticipation&&<><TableCell align="right">Sum</TableCell><TableCell align="right">Avg / Rate</TableCell></>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.map((r) => (
                    <TableRow
                      key={r.orgPath}
                      sx={r.level === 'root' ? { bgcolor: 'action.hover' } : {}}
                      hover
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={r.level === 'root' ? 700 : 400}
                          sx={r.level === 'child' ? { pl: 2 } : {}}
                        >
                          {r.orgPath}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{r.count.toLocaleString()}</TableCell>
                      <>{!isParticipation&&<><TableCell align="right">{r.sum?.toLocaleString()}</TableCell><TableCell align="right" sx={{ fontWeight: 600 }}>{pct(r.avg??0, metric)}</TableCell></>}</>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </Box>
  );
}
